import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import SelectForm from '../../../organisms/SelectForm';
import NewTooltip from '../../../atoms/NewTooltip';
import { DealItem } from '../../../../types/Contact';

interface Props {
  readonly deals: DealItem[];
  readonly contactId: string;
  readonly selectedIds: Set<string>;
  readonly onSelectionChange: (ids: Set<string>) => void;
}

export default function ContactDealsTab({ deals, contactId, selectedIds, onSelectionChange }: Props) {
  const navigate = useNavigate();
  const [filterOperation, setFilterOperation] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterOverdue, setFilterOverdue] = useState('');

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const operations = useMemo(() => Array.from(new Set(deals.map(d => d.operation))), [deals]);
  const statuses = useMemo(() => Array.from(new Set(deals.map(d => d.status))), [deals]);

  const filteredDeals = useMemo(() => {
    return deals.filter(d => {
      if (filterOperation && d.operation !== filterOperation) return false;
      if (filterStatus && d.status !== filterStatus) return false;
      if (filterOverdue === 'yes' && new Date(d.dueDate) >= today) return false;
      if (filterOverdue === 'no' && new Date(d.dueDate) < today) return false;
      return true;
    });
  }, [deals, filterOperation, filterStatus, filterOverdue, today]);

  const toggleSelect = (id: string) => {
    const next = new Set(selectedIds);
    next.has(id) ? next.delete(id) : next.add(id);
    onSelectionChange(next);
  };

  const allFilteredSelected = filteredDeals.length > 0 && filteredDeals.every(d => selectedIds.has(d.id));

  const toggleAll = () => {
    const next = new Set(selectedIds);
    if (allFilteredSelected) {
      filteredDeals.forEach(d => next.delete(d.id));
    } else {
      filteredDeals.forEach(d => next.add(d.id));
    }
    onSelectionChange(next);
  };

  return (
    <div>
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          {operations.length > 1 && (
            <NewTooltip content="Filtrar por operação de crédito" alignment="top">
              <div className="w-48">
                <SelectForm id="filter-operation" label="Operação" value={filterOperation} onChange={setFilterOperation} defaultVoidOption defaultLabel="Todas" options={operations.map(op => ({ value: op, label: op }))} />
              </div>
            </NewTooltip>
          )}
          {statuses.length > 1 && (
            <NewTooltip content="Filtrar por status de validação" alignment="top">
              <div className="w-40">
                <SelectForm id="filter-status" label="Status" value={filterStatus} onChange={setFilterStatus} defaultVoidOption defaultLabel="Todos" options={statuses.map(s => ({ value: s, label: s }))} />
              </div>
            </NewTooltip>
          )}
          <NewTooltip content="Filtrar por vencimento em relação a hoje" alignment="top">
            <div className="w-36">
              <SelectForm id="filter-overdue" label="Vencimento" value={filterOverdue} onChange={setFilterOverdue} defaultVoidOption defaultLabel="Todos" options={[{ value: 'yes', label: 'Vencida' }, { value: 'no', label: 'A vencer' }]} />
            </div>
          </NewTooltip>
        </div>
        {filteredDeals.length > 0 && (
          <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 cursor-pointer">
            <input type="checkbox" checked={allFilteredSelected} onChange={toggleAll} className="w-4 h-4 text-blue-600 rounded" />
            Selecionar todos
          </label>
        )}
      </div>

      {/* Deals grid */}
      <div className="grid grid-cols-3 gap-3">
        {filteredDeals.map((deal) => {
          const isSelected = selectedIds.has(deal.id);
          return (
            <div key={deal.id} role="checkbox" aria-checked={isSelected} tabIndex={0} onClick={() => toggleSelect(deal.id)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSelect(deal.id); } }} className={`bg-white dark:bg-slate-800 border-2 rounded-lg p-3 cursor-pointer transition-colors ${isSelected ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600'}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-1.5 text-xs">
                  <input type="checkbox" checked={isSelected} onChange={() => {}} className="w-3.5 h-3.5 text-blue-600 rounded pointer-events-none" />
                  <button onClick={(e) => { e.stopPropagation(); navigate(`/operations/${deal.operation}`); }} className="text-blue-600 dark:text-blue-400 hover:underline font-medium">{deal.operation}</button>
                  <span className="text-gray-400">•</span>
                  <span className="text-gray-700 dark:text-gray-300 font-medium">Nº {deal.number}</span>
                </div>
                <span className={`px-1.5 py-0.5 rounded-full text-xs font-medium ${deal.status === 'Validada' ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' : 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'}`}>{deal.status}</span>
              </div>
              <p className="text-sm text-gray-900 dark:text-white truncate mb-1" title={deal.assignor}>{deal.assignor}</p>
              <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                <span className="text-sm font-semibold text-gray-900 dark:text-white">{deal.valueFormatted ?? deal.value}</span>
                <span>Venc. {new Date(deal.dueDate).toLocaleDateString('pt-BR')}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
